import React from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

function SkeletonLine({ width = "100%", height = 18 }) {
  return <Skeleton variant="text" width={width} height={height} sx={{ borderRadius: "4px" }} />;
}

function SkeletonChip({ width = 72 }) {
  return (
    <Skeleton variant="rounded" width={width} height={24} sx={{ borderRadius: "20px" }} />
  );
}

/** Full customer detail page (632px content column). */
export function CustomerDetailPageSkeleton() {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#FFF",
      }}
    >
      <Box sx={{ bgcolor: "#FFF", px: 3, py: 2, borderBottom: "1px solid #E5E9EC", flexShrink: 0 }}>
        <Box sx={{ maxWidth: 632, mx: "auto", display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton variant="circular" width={56} height={56} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={180} height={28} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="text" width="45%" height={18} sx={{ borderRadius: "4px", mt: 0.5 }} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", pt: "32px", px: 3, pb: 3 }}>
        <Box sx={{ maxWidth: 632, width: "100%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={200}
            sx={{ borderRadius: "8px", mb: 3 }}
          />
          <Skeleton
            variant="rounded"
            width="100%"
            height={120}
            sx={{ borderRadius: "8px", mb: 3 }}
          />
          <Box sx={{ mb: 2.5 }}>
            <SkeletonLine width={60} height={16} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
              <SkeletonChip />
              <SkeletonChip width={96} />
            </Box>
          </Box>
          <Box>
            <SkeletonLine width={80} height={16} />
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                <Skeleton variant="circular" width={16} height={16} />
                <SkeletonLine width={`${70 - i * 8}%`} height={14} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** 280px customer detail sidebar body + footer. */
export function CustomerSidebarSkeleton() {
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <SkeletonLine width="70%" height={16} />
            <SkeletonLine width="50%" height={14} />
          </Box>
        </Box>
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ py: 1.5, borderBottom: "1px solid #EEF2F6" }}>
            <SkeletonLine width={120} height={16} />
            <Box sx={{ mt: 1 }}>
              <SkeletonLine width="85%" height={14} />
              {i === 1 && (
                <>
                  <SkeletonLine width="70%" height={14} />
                  <SkeletonLine width="60%" height={14} />
                </>
              )}
              {i > 1 && (
                <Box sx={{ display: "flex", gap: 0.75, mt: 0.75 }}>
                  <SkeletonChip />
                  {i < 4 && <SkeletonChip width={88} />}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: "1px solid #EEF2F6",
          display: "flex",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        <Skeleton variant="rounded" width={160} height={40} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="rounded" width={80} height={40} sx={{ borderRadius: "8px" }} />
      </Box>
    </Box>
  );
}

/** Add customer modal form fields. */
export function CustomerAddModalFormSkeleton() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {[1, 2, 3, 4].map((i) => (
        <Box key={i}>
          <SkeletonLine width={60} height={12} />
          <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "8px", mt: 0.75 }} />
        </Box>
      ))}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <SkeletonLine width={48} height={12} />
          <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "8px", mt: 0.75 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SkeletonLine width={40} height={12} />
          <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "8px", mt: 0.75 }} />
        </Box>
      </Box>
    </Box>
  );
}

export function CustomerEditPageSkeleton() {
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 3, bgcolor: "#FFF" }}>
      <Box sx={{ maxWidth: 632, width: "100%", mx: "auto" }}>
        <Skeleton variant="text" width={180} height={32} sx={{ borderRadius: "4px" }} />
        <Skeleton variant="text" width="85%" height={18} sx={{ borderRadius: "4px", mt: 1, mb: 3 }} />
        <Skeleton variant="circular" width={88} height={88} sx={{ mb: 3 }} />
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width={60} height={14} sx={{ mb: 0.75 }} />
            <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "8px" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function CustomerFormFieldSkeleton({ height = 40 }) {
  return (
    <Skeleton variant="rounded" width="100%" height={height} sx={{ borderRadius: "8px" }} />
  );
}
